import {
  BigBodyGroup,
  Exercise,
  Level,
  UserProfile,
  WorkoutSettings,
} from 'fitbeat.gym-core';
import {
  BaseEquipment,
  CardioOrResistance,
  EnabledFeatures,
  EquipmentCombinations,
  IEnabledFeatures,
} from 'fitbeat.models';
import React, { FunctionComponent, useState } from 'react';
import { translate } from 'react-i18next';
import {
  StyleProp, StyleSheet, Text, TextStyle, View, ViewStyle,
} from 'react-native';
import { connect } from 'react-redux';
import { enabledFeaturesSelector } from '../../../auth/auth-selectors';
import { Orientation } from '../../../HomeWorkouts';
import Theme, { ThemeType } from '../../../theme';
import { ExclamationIcon } from '../../../ui/components/Icons';
import { defaultScale } from '../../../utils/scaling';
import { openPersonaliseWorkoutSetupScreen } from '../../actions';
import { BodyGroupSelection } from '../../components/BodyGroupSelection';
import { BookingInstructionHeader, BookingInstructionSubtitle } from '../../components/BookingInstructions';
import { EquipmentSelection } from '../../components/EquipmentSelection';
import { SkillLevelSelection } from '../../components/SkillLevelSelection';
import { WorkoutTypeSelection } from '../../components/WorkoutTypeSelection';
import { AllowBagContactSelection } from '../../components/AllowBagContactSelection';
import { personaliseSetupScreenSelector, personaliseSetupWarningSelector } from '../../selectors';
import { PersonaliseSetupScreens } from './types';

import { PersonaliseWorkoutOverview } from './PersonaliseWorkoutOverview';
import { SetupHeaderTitle } from './SetupHeaderTitle';

export interface IProps {
  themeType: ThemeType;
  orientation: Orientation;
  gym: any;
  workoutSettings: any;
  userProfile: UserProfile;
  enabledFeatures: IEnabledFeatures;
  currentPersonaliseSetupScreen: PersonaliseSetupScreens | null;
  personaliseSetupWarning: PersonaliseSetupWarning | null;
  prepAndOpenPersonaliseSetupScreen(screen: PersonaliseSetupScreens, workoutSettings: WorkoutSettings,
    gymType: any, allowToSetEquipment: any, userProfile: UserProfile);
  onCompleteSettingChange(workoutSettings: WorkoutSettings, hasWarning?: boolean): void;
  t(key: string, options?: any): string;
}

export enum PersonaliseSetupWarning {
  CustomiseWarning = 'CustomiseWarning',
  BodyGroupEquipmentWarning = 'BodyGroupEquipmentWarning',
  EquipmentWarning = 'EquipmentWarning',
  BodyGroupBalanceWarning = 'bodyGroupBalanceWarning',
  BagContactBalanceWarning = 'BagContactBalanceWarning',
}

function SkillLevelComponent({
  t, themeType, workoutSettings, changeSetting,
}) {
  const [skillLevel, setSkillLevel] = useState<Level | null>(workoutSettings.level);
  const changeSkillLevelSetting = (level) => {
    changeSetting(PersonaliseSetupScreens.SkillLevel, level);
    setSkillLevel(level);
  };
  const themeStyle = themeStyleMapper[themeType];
  return (
    <View style={[styles.centerContainer, styles.alignCenter]}>
      <BookingInstructionHeader
        style={[styles.selectionHeading, themeStyle.textColor1]}
        header={t('selectSkillLevelSelection')}
      />
      <SkillLevelSelection
        theme={themeType}
        skillLevel={skillLevel}
        onPress={async (level) => changeSkillLevelSetting(level)}
      />
      <BookingInstructionSubtitle style={themeStyle.textColor2} header={t('adjustsDifficulty')} />
    </View>
  );
}

function EquipmentComponent({
  t, themeType, workoutSettings, changeSetting, enabledFeatures, userProfile,
}) {
  const { equipment = [] } = workoutSettings;
  const toggleEquipment = (equipmentList, shouldSetDefaultEquipment: boolean) => {
    changeSetting(PersonaliseSetupScreens.Equipment, equipmentList, shouldSetDefaultEquipment);
  };
  const themeStyle = themeStyleMapper[themeType];
  return (
    <EquipmentSelection
      sectionHeading={() => (
        <BookingInstructionHeader
          style={[styles.selectionHeading, themeStyle.textColor1]}
          header={t('selectYourEquipment')}
        />
      )}
      theme={themeType}
      equipment={equipment}
      onToggleEquipment={toggleEquipment}
      t={t}
      enabledFeatures={enabledFeatures}
      defaultEquipment={userProfile.defaultEquipment}
      workoutSettings={workoutSettings}
      userProfile={userProfile}
    />
  );
}

function WorkoutTypeComponent({
  t, themeType, workoutSettings, changeSetting, userProfile, gym, enabledFeatures,
}) {
  const [workoutType, setWorkoutType] = useState<CardioOrResistance | null>(workoutSettings.cardioOrResistance);
  const changeWorkoutTypeSetting = (type) => {
    changeSetting(PersonaliseSetupScreens.WorkoutType, type);
    setWorkoutType(type);
  };
  const themeStyle = themeStyleMapper[themeType];
  return (
    <View style={[styles.centerContainer, styles.alignCenter]}>
      <BookingInstructionHeader
        style={[styles.selectionHeading, themeStyle.textColor1]}
        header={t('selectWorkoutTypeSelection')}
      />
      <WorkoutTypeSelection
        t={t}
        theme={themeType}
        workoutType={workoutType}
        onPress={async (type) => changeWorkoutTypeSetting(type)}
        userProfile={userProfile}
        workoutSettings={workoutSettings}
        gym={gym}
        enabledFeatures={enabledFeatures}
      />
    </View>
  );
}

function BagContactComponent({
  t, themeType, userProfile, workoutSettings, changeSetting, orientation,
}) {
  const [allowBagContact, setBagContactSetting] = useState<CardioOrResistance | null>(workoutSettings.allowBagContact);
  const changeBagContactSetting = (setting) => {
    changeSetting(PersonaliseSetupScreens.BagContact, setting);
    setBagContactSetting(allowBagContact);
  };
  const themeStyle = themeStyleMapper[themeType];
  return (
    <View style={{ flex: 1, justifyContent: 'center' }}>
      <BookingInstructionHeader
        style={[styles.selectionHeading, themeStyle.textColor1]}
        header={t('selectBagContactSelection')}
      />
      <AllowBagContactSelection
        t={t}
        theme={themeType}
        allowBagContactSetting={workoutSettings.allowBagContact}
        onPress={async (setting) => changeBagContactSetting(setting)}
      />
      <BookingInstructionSubtitle
        style={themeStyle.textColor2}
        header={workoutSettings.allowBagContact ? t('bagContactDescription') : t('noBagContactDescription')}
      />
    </View>
  );
}

function BodyGroupComponent({
  t, themeType, userProfile, workoutSettings, changeSetting, orientation,
}) {
  const { gender } = userProfile;
  const { cardioOrResistance, threeBodyGroups } = workoutSettings;
  const current = Exercise.getCurrentBodyGroupFrom3BodyGroups(threeBodyGroups);
  const [bodyGroup, setBodyGroup] = useState<any | null>(current);
  const changeBodyGroupSetting = (setting) => {
    changeSetting(PersonaliseSetupScreens.BodyGroup, setting);
    const bodyGroupSetting = Exercise.getCurrentBodyGroupFrom3BodyGroups(setting);
    setBodyGroup(bodyGroupSetting);
  };
  const themeStyle = themeStyleMapper[themeType];
  return (
    <View style={{ flex: 1, justifyContent: 'center' }}>
      <View>
        <BookingInstructionHeader
          style={[{ paddingBottom: 0 }, themeStyle.textColor1]}
          header={t('selectBodyGroupSelection')}
        />
        <BodyGroupSelection
          t={t}
          orientation={orientation}
          theme={themeType}
          bodyGroup={bodyGroup}
          cardioOrResistance={cardioOrResistance}
          gender={gender}
          onPress={async (setting) => changeBodyGroupSetting(setting)}
        />
      </View>
    </View>
  );
}

function WarningComponent({ t, themeType, personaliseSetupWarning }) {
  const themeStyle = themeStyleMapper[themeType];
  let warningText = t('customiseWarning');
  if (personaliseSetupWarning === PersonaliseSetupWarning.BodyGroupEquipmentWarning) {
    warningText = t('bodyGroupEquipmentWarning');
  } else if (personaliseSetupWarning === PersonaliseSetupWarning.EquipmentWarning) {
    warningText = t('equipmentWarning');
  } else if (personaliseSetupWarning === PersonaliseSetupWarning.BagContactBalanceWarning) {
    warningText = t('bagContactBalanceWarning');
  } else if (personaliseSetupWarning === PersonaliseSetupWarning.BodyGroupBalanceWarning) {
    warningText = t('bodyGroupBalanceWarning');
  }
  return (
    <View style={styles.centerContainer}>
      <View style={[styles.warningContainer, themeStyle.container]}>
        <View style={styles.exclamationContainer}>
          <ExclamationIcon size={defaultScale(40)} fontSize={defaultScale(20)} fontWeight='normal' />
        </View>
        {personaliseSetupWarning === PersonaliseSetupWarning.CustomiseWarning
          && <Text style={[styles.warningText, themeStyle.textColor1]}>{t('warning')}</Text>}
        <View style={styles.warningExplanationContainer}>
          <Text style={[styles.warningExplanation, themeStyle.textColor1]}>
            {warningText}
          </Text>
        </View>
      </View>
    </View>
  );
}

const PersonaliseScreenComponent = (props) => ({
  [PersonaliseSetupScreens.Overview]: <PersonaliseWorkoutOverview {...props} />,
  [PersonaliseSetupScreens.SkillLevel]: <SkillLevelComponent {...props} />,
  [PersonaliseSetupScreens.Equipment]: <EquipmentComponent {...props} />,
  [PersonaliseSetupScreens.WorkoutType]: <WorkoutTypeComponent {...props} />,
  [PersonaliseSetupScreens.BodyGroup]: <BodyGroupComponent {...props} />,
  [PersonaliseSetupScreens.BagContact]: <BagContactComponent {...props} />,
});

function PersonaliseWorkoutSetupComponent({
  t,
  themeType = ThemeType.light,
  orientation,
  gym,
  workoutSettings,
  userProfile,
  enabledFeatures,
  currentPersonaliseSetupScreen,
  personaliseSetupWarning,
  prepAndOpenPersonaliseSetupScreen,
  onCompleteSettingChange,
}: IProps) {
  const changeSetting = (setupScreen: PersonaliseSetupScreens, setting, shouldSaveDefaultEquipment?: boolean) => {
    switch (setupScreen) {
      case PersonaliseSetupScreens.SkillLevel:
        workoutSettings = {
          ...workoutSettings,
          level: setting,
        };
        break;
      case PersonaliseSetupScreens.WorkoutType:
        let newSettings = {};
        if (setting === CardioOrResistance.Balance) {
          newSettings = {
            threeBodyGroups: WorkoutSettings.getFullBody(),
          };
        }
        workoutSettings = {
          ...workoutSettings,
          ...newSettings,
          cardioOrResistance: setting,
        };
        break;
      case PersonaliseSetupScreens.BodyGroup:
        workoutSettings = {
          ...workoutSettings,
          threeBodyGroups: setting.sort(),
        };
        break;
      case PersonaliseSetupScreens.Equipment:
        const threeBodyGroups = BaseEquipment.getEquipmentCombination(setting) === EquipmentCombinations.BODYWEIGHT
          ? WorkoutSettings.getFullBody() : workoutSettings.threeBodyGroups;
        workoutSettings = {
          ...workoutSettings,
          equipment: [...setting].sort(),
          threeBodyGroups,
        };
        break;
      case PersonaliseSetupScreens.BagContact:
        workoutSettings = {
          ...workoutSettings,
          allowBagContact: setting,
        };
        break;
      default:
        break;
    }

    onCompleteSettingChange(workoutSettings, shouldSaveDefaultEquipment);
  };

  const prepAndOpenSetupScreen = (screen: PersonaliseSetupScreens) => {
    let gymType;
    if (gym) { gymType = gym.type; }
    let allowToSetEquipment: boolean | undefined;
    if (userProfile) { allowToSetEquipment = EnabledFeatures.canSelectEquipment(enabledFeatures); }
    prepAndOpenPersonaliseSetupScreen(screen, workoutSettings, gymType, allowToSetEquipment, userProfile);
  };

  if (personaliseSetupWarning === PersonaliseSetupWarning.CustomiseWarning
    || personaliseSetupWarning === PersonaliseSetupWarning.BodyGroupEquipmentWarning
    || personaliseSetupWarning === PersonaliseSetupWarning.EquipmentWarning
    || personaliseSetupWarning === PersonaliseSetupWarning.BagContactBalanceWarning
    || personaliseSetupWarning === PersonaliseSetupWarning.BodyGroupBalanceWarning) {
    return <WarningComponent {...{ t, themeType, personaliseSetupWarning }} />;
  }

  let ScreenComponent;
  if (currentPersonaliseSetupScreen) {
    ScreenComponent = PersonaliseScreenComponent({
      t,
      themeType,
      orientation,
      workoutSettings,
      userProfile,
      gym,
      openPersonaliseSetupScreen: prepAndOpenSetupScreen,
      changeSetting,
      enabledFeatures,
    })[currentPersonaliseSetupScreen];
  }

  return <>{ScreenComponent}</>;
}

const mapStateToProps = (state): Partial<IProps> => ({
  currentPersonaliseSetupScreen: personaliseSetupScreenSelector(state),
  personaliseSetupWarning: personaliseSetupWarningSelector(state),
  enabledFeatures: enabledFeaturesSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  prepAndOpenPersonaliseSetupScreen: (
    screen,
    workoutSettings,
    gymType,
    allowToSetEquipment,
    userProfile,
  ) => dispatch(openPersonaliseWorkoutSetupScreen(screen, workoutSettings, gymType, allowToSetEquipment, userProfile)),
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  alignCenter: {
    alignItems: 'center',
  },
  backButton: {
    marginRight: defaultScale(5),
    alignSelf: 'stretch',
    flex: 1,
  },
  nextContainer: {
    flex: 2,
  },
  bottom: {
    justifyContent: 'flex-end',
  },
  footerButtons: {
    marginBottom: defaultScale(10),
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  selectionHeading: {
    ...Theme.textStyle.heading,
  },
  warningContainer: {
    flex: 1,
    justifyContent: 'center',
    alignSelf: 'center',
    alignItems: 'center',
    width: '100%',
  },
  exclamationContainer: {
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: defaultScale(15),
    marginBottom: defaultScale(20),
  },
  warningText: {
    ...Theme.textStyle.heading,
    alignSelf: 'center',
    marginBottom: defaultScale(20),
  },
  warningExplanation: {
    ...Theme.textStyle.heading,
    alignSelf: 'center',
    textAlign: 'center',
  },
  warningExplanationContainer: {
    width: '70%',
    alignSelf: 'center',
  },
});
export interface IThemeStyle {
  container: StyleProp<ViewStyle>;
  textColor1: StyleProp<TextStyle>;
  textColor2: StyleProp<TextStyle>;
  textColor3: StyleProp<TextStyle>;
  textColor4: StyleProp<TextStyle>;
  themeColor1: string;
  themeColor1Inverse: string;
  themeColor2: string;
  themeColor3: string;
  themeColor4: string;
  border: StyleProp<ViewStyle>;
  border2: StyleProp<ViewStyle>;
  bodyImageColor2: string;
  heading: StyleProp<ViewStyle>;
}

export const themeStyleMapper: { [key in ThemeType]: IThemeStyle } = {
  [ThemeType.light]: {
    container: {
      backgroundColor: Theme.WHITE,
    },
    textColor1: {
      color: Theme.BLACK,
    },
    textColor2: {
      color: Theme.GREY_DARK_MEDIUM,
    },
    textColor3: {
      color: Theme.GREY_DARK_MEDIUM,
    },
    textColor4: {
      color: Theme.CHARCOAL,
    },
    themeColor1: Theme.BLACK,
    themeColor1Inverse: Theme.WHITE,
    themeColor2: Theme.GREY_DARK_MEDIUM,
    themeColor3: Theme.CHARCOAL,
    themeColor4: Theme.GREY_DARK,
    bodyImageColor2: Theme.GREY,
    border: {
      borderWidth: 2,
      borderColor: Theme.GREY_LIGHT,
    },
    border2: {
      borderWidth: 2,
      borderColor: Theme.GREY_LIGHT,
    },
    heading: {
      paddingTop: 0,
    },
  },
  [ThemeType.dark]: {
    container: {
      backgroundColor: Theme.BLACK,
    },
    textColor1: {
      color: Theme.WHITE,
    },
    textColor2: {
      color: Theme.GREY_MEDIUM_LIGHT,
    },
    textColor3: {
      color: Theme.GREY_MEDIUM_LIGHT,
    },
    textColor4: {
      color: Theme.WHITE,
    },
    themeColor1: Theme.WHITE,
    themeColor1Inverse: Theme.BLACK,
    themeColor2: Theme.GREY_MEDIUM_LIGHT,
    themeColor3: Theme.WHITE,
    themeColor4: Theme.WHITE,
    bodyImageColor2: Theme.GREY_DARK_MEDIUM,
    border: {
      borderWidth: 2,
      borderColor: Theme.WHITE,
    },
    border2: {
      borderWidth: 1,
      borderColor: Theme.GREY_MEDIUM,
    },
    heading: {
      paddingTop: 0,
    },
  },
};

export const PersonaliseWorkoutSetup = connect(mapStateToProps, mapDispatchToProps)(
  translate(['bookings', 'common', 'settings'], { wait: true })(PersonaliseWorkoutSetupComponent),
);
